import React, { useState, useEffect } from "react";
import { Card, Tag } from "antd";
import {
  BarChartOutlined,
  MessageOutlined,
  StarFilled,
  EyeOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  FilePdfOutlined,
  TrophyOutlined,
  QrcodeOutlined,
  CloseOutlined,
  FullscreenOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useLanguage } from "../../../context/LanguageContext";

export default function DashboardPreviewSection() {
  const { language } = useLanguage();
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Intersection Observer refs - vereinfacht
  const [refHeader, inViewHeader] = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });
  const [refMainFeatures, inViewMainFeatures] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [refFeatureCards, inViewFeatureCards] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Escape key handler
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && fullscreenImage) {
        closeFullscreen();
      }
    };

    if (fullscreenImage) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [fullscreenImage]);

  // Framer Motion Variants - vereinfacht
  const fadeInUp = (delay = 0) => ({
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    },
  });

  const fadeInLeft = (delay = 0) => ({
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    },
  });

  const fadeInRight = (delay = 0) => ({
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: "easeOut", delay },
    },
  });

  const scaleIn = (delay = 0) => ({
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut", delay },
    },
  });

  const content = {
    de: {
      badge: "Feedback Analyse Plattform",
      title: "Dashboard & Formulare",
      titleHighlight: "perfekt vereint",
      subtitle:
        "Erstellen Sie professionelle Feedback-Formulare, teilen Sie diese per QR-Code oder Link, und analysieren Sie Ihre Daten mit unserem intelligenten Dashboard – alles in einer modernen Plattform.",
      dashboard: {
        title: "Analytics Dashboard",
        subtitle: "Echtzeit Einblicke & KI-Analyse",
        stats: {
          responses: "Antworten",
          satisfaction: "Zufriedenheit",
          growth: "Wachstum",
        },
      },
      form: {
        title: "Feedback Formular",
        subtitle: "Interaktiv & Ansprechend",
        rating: "5.0 Bewertung",
      },
      features: {
        qr: {
          title: "QR-Code & Link Sharing",
          description:
            "Teilen Sie Formulare einfach per QR-Code oder direkten Link für maximale Reichweite",
        },
        analytics: {
          title: "KI-Powered Reports",
          description:
            "Automatische PDF-Berichte mit KI-Analyse und detaillierten Insights",
        },
        incentives: {
          title: "Gewinnspiel-Anreize",
          description:
            "Steigern Sie die Teilnahme mit optionalen Gewinnspielen und Belohnungen",
        },
        realtime: {
          title: "Echtzeit Dashboard",
          description:
            "Verfolgen Sie Feedback live mit interaktiven Charts und Visualisierungen",
        },
      },
    },
    en: {
      badge: "Feedback Analysis Platform",
      title: "Dashboard & Forms",
      titleHighlight: "perfectly united",
      subtitle:
        "Create professional feedback forms, share them via QR code or link, and analyze your data with our intelligent dashboard – all in one modern platform.",
      dashboard: {
        title: "Analytics Dashboard",
        subtitle: "Real-time Insights & AI Analysis",
        stats: {
          responses: "Responses",
          satisfaction: "Satisfaction",
          growth: "Growth",
        },
      },
      form: {
        title: "Feedback Form",
        subtitle: "Interactive & Engaging",
        rating: "5.0 Rating",
      },
      features: {
        qr: {
          title: "QR Code & Link Sharing",
          description:
            "Share forms easily via QR code or direct link for maximum reach",
        },
        analytics: {
          title: "AI-Powered Reports",
          description:
            "Automatic PDF reports with AI analysis and detailed insights",
        },
        incentives: {
          title: "Contest Incentives",
          description: "Boost participation with optional contests and rewards",
        },
        realtime: {
          title: "Real-time Dashboard",
          description:
            "Track feedback live with interactive charts and visualizations",
        },
      },
    },
  };

  const localContent = content[language] || content.de;

  // Function to open fullscreen image
  const openFullscreen = (imageSrc, imageAlt) => {
    setFullscreenImage({ src: imageSrc, alt: imageAlt });
    setImageLoaded(false);
    setZoom(1);
  };

  // Function to close fullscreen modal
  const closeFullscreen = () => {
    setFullscreenImage(null);
    setImageLoaded(false);
    setZoom(1);
  };

  // Zoom functions
  const zoomIn = () => setZoom((prev) => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoom((prev) => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setZoom(1);

  return (
    <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-orange-50/50 via-white to-orange-50/30">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-300/15 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          ref={refHeader}
          variants={fadeInUp(0)}
          initial="hidden"
          animate={inViewHeader ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.div
            variants={scaleIn(0.2)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full mb-6 border border-orange-200"
          >
            <span className="inline-flex">
              <ThunderboltOutlined className="text-orange-600" />
            </span>
            <span className="text-sm font-semibold text-orange-700">
              {localContent.badge}
            </span>
          </motion.div>
          <motion.h2
            variants={fadeInUp(0.3)}
            className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 text-balance"
          >
            {localContent.title}{" "}
            <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
              {localContent.titleHighlight}
            </span>
          </motion.h2>
          <motion.p
            variants={fadeInUp(0.4)}
            className="text-xl text-gray-600 max-w-3xl mx-auto text-pretty leading-relaxed"
          >
            {localContent.subtitle}
          </motion.p>
        </motion.div>

        {/* Main Feature Showcase */}
        <div ref={refMainFeatures} className="grid lg:grid-cols-2 gap-8 mb-20">
          {/* Dashboard Preview */}
          <motion.div
            variants={fadeInLeft(0)}
            initial="hidden"
            animate={inViewMainFeatures ? "visible" : "hidden"}
            className="relative group h-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-all duration-700" />
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-orange-100 transform group-hover:scale-[1.02] transition-all duration-500 h-full flex flex-col">
              <div className="p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg grid place-items-center flex-shrink-0">
                    <BarChartOutlined className="text-white text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {localContent.dashboard.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {localContent.dashboard.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <div className="p-6 flex-1 relative group/img">
                  <div
                    className="relative cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      console.log("Dashboard image clicked"); // Debug log
                      openFullscreen(
                        "/dashboard.png",
                        "Dashboard Analytics Preview"
                      );
                    }}
                  >
                    <img
                      src="/dashboard.png"
                      alt="Dashboard Analytics Preview"
                      width={600}
                      height={300}
                      className="w-full object-contain rounded-lg hover:opacity-90 transition-all duration-300 group-hover/img:shadow-xl"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all duration-300 bg-black/10 rounded-lg pointer-events-none">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <FullscreenOutlined className="text-orange-600 text-xl" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 flex-shrink-0">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        2.4k
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        {localContent.dashboard.stats.responses}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        94%
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        {localContent.dashboard.stats.satisfaction}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-orange-600">
                        +18%
                      </div>
                      <div className="text-xs text-gray-500 font-medium">
                        {localContent.dashboard.stats.growth}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Form Preview */}
          <motion.div
            variants={fadeInRight(0.2)}
            initial="hidden"
            animate={inViewMainFeatures ? "visible" : "hidden"}
            className="relative group h-full"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-all duration-700" />
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden border border-orange-100 transform group-hover:scale-[1.02] transition-all duration-500 h-full flex flex-col">
              <div className="p-6 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg grid place-items-center flex-shrink-0">
                    <MessageOutlined className="text-white text-lg" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-gray-900 text-lg">
                      {localContent.form.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {localContent.form.subtitle}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <div className="p-6 flex-1 relative group/img">
                  <div
                    className="relative cursor-pointer"
                    onClick={() => {
                      console.log("Form image clicked"); // Debug
                      openFullscreen("/form.png", "Feedback Form Preview");
                    }}
                  >
                    <img
                      src="/form.png"
                      alt="Feedback Form Preview"
                      width={600}
                      height={300}
                      className="w-full object-contain rounded-lg hover:opacity-90 transition-all duration-300 group-hover/img:shadow-xl"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-all duration-300 bg-black/10 rounded-lg pointer-events-none">
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                        <FullscreenOutlined className="text-orange-600 text-xl" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <StarFilled
                          key={i}
                          className="text-orange-500 text-base"
                        />
                      ))}
                    </div>
                    <Tag className="bg-orange-100 text-orange-700 border-orange-200 rounded-full px-3 py-1 font-medium">
                      {localContent.form.rating}
                    </Tag>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Feature Cards */}
        <div
          ref={refFeatureCards}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
        >
          {[
            {
              icon: QrcodeOutlined,
              feature: "qr",
              delay: 0,
            },
            {
              icon: FilePdfOutlined,
              feature: "analytics",
              delay: 0.1,
            },
            {
              icon: TrophyOutlined,
              feature: "incentives",
              delay: 0.2,
            },
            {
              icon: EyeOutlined,
              feature: "realtime",
              delay: 0.3,
            },
          ].map(({ icon: Icon, feature, delay }, index) => (
            <motion.div
              key={feature}
              variants={fadeInUp(delay)}
              initial="hidden"
              animate={inViewFeatureCards ? "visible" : "hidden"}
            >
              <Card
                hoverable
                className="group transition-all duration-300 bg-white border-0 hover:shadow-xl hover:bg-orange-50 h-full cursor-default"
              >
                <div className="p-6 text-center h-full flex flex-col justify-center">
                  <div className="w-14 h-14 bg-orange-100 rounded-2xl grid place-items-center mx-auto mb-4 group-hover:bg-orange-200 transition-colors">
                    <Icon className="text-orange-600 text-2xl" />
                  </div>
                  <h4 className="font-bold text-gray-900 mb-2">
                    {localContent.features[feature].title}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {localContent.features[feature].description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Enhanced Fullscreen Modal */}
      <AnimatePresence>
        {fullscreenImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            style={{
              background:
                "linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(20, 20, 20, 0.98))",
            }}
            onClick={closeFullscreen}
          >
            {/* Background blur effect */}
            <div className="absolute inset-0 backdrop-blur-xl" />

            {/* Animated background particles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-orange-500/20 rounded-full"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                  animate={{
                    y: [0, -30, 0],
                    opacity: [0, 1, 0],
                  }}
                  transition={{
                    duration: 3 + Math.random() * 2,
                    repeat: Infinity,
                    delay: Math.random() * 2,
                  }}
                />
              ))}
            </div>

            {/* Close button */}
            <motion.button
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              onClick={closeFullscreen}
              className="absolute top-6 right-6 z-50 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full p-3 shadow-2xl hover:shadow-orange-500/25 transition-all duration-300"
            >
              <CloseOutlined className="text-xl" />
            </motion.button>

            {/* Zoom controls */}
            <motion.div
              initial={{ scale: 0, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="absolute top-6 left-6 z-50 flex gap-2"
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  zoomOut();
                }}
                className="bg-white/10 backdrop-blur-md text-white rounded-full p-3 hover:bg-white/20 transition-all duration-300"
              >
                <ZoomOutOutlined className="text-lg" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  resetZoom();
                }}
                className="bg-white/10 backdrop-blur-md text-white rounded-full px-4 py-3 hover:bg-white/20 transition-all duration-300 text-sm font-medium"
              >
                {Math.round(zoom * 100)}%
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={(e) => {
                  e.stopPropagation();
                  zoomIn();
                }}
                className="bg-white/10 backdrop-blur-md text-white rounded-full p-3 hover:bg-white/20 transition-all duration-300"
              >
                <ZoomInOutlined className="text-lg" />
              </motion.button>
            </motion.div>

            {/* Image container */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative max-w-[90vw] max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Loading skeleton */}
              {!imageLoaded && (
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse rounded-2xl" />
              )}

              {/* Main image */}
              <motion.img
                src={fullscreenImage.src}
                alt={fullscreenImage.alt}
                onLoad={() => setImageLoaded(true)}
                className="max-w-full max-h-[85vh] object-contain rounded-2xl shadow-2xl transition-all duration-500"
                style={{
                  transform: `scale(${zoom})`,
                  filter: imageLoaded ? "none" : "blur(5px)",
                  opacity: imageLoaded ? 1 : 0,
                }}
                drag
                dragConstraints={{
                  left: -100,
                  right: 100,
                  top: -100,
                  bottom: 100,
                }}
                whileDrag={{ cursor: "grabbing" }}
              />

              {/* Image border glow */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-500/20 via-transparent to-orange-600/20 blur-xl -z-10" />
            </motion.div>

            {/* Image info */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-white/10 backdrop-blur-md text-white px-6 py-3 rounded-full shadow-lg"
            >
              <p className="text-sm font-medium">{fullscreenImage.alt}</p>
            </motion.div>

            {/* Keyboard hint */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 2 }}
              className="absolute bottom-6 right-6 text-white/60 text-xs"
            >
              Press ESC to close
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
